// NOTE: This file now relies on the injected `callApi` helper from
// `useAuthenticatedApi`, so we no longer import `apiClient` directly.

// These helpers wrap authenticated API calls for housekeeping.
// A `callApi` function (from useAuthenticatedApi) is injected so that we
// always hit the backend with the user's auth headers.

export type CallApiFn = (config: {
  method: "GET" | "POST" | "PUT" | "DELETE";
  url: string;
  data?: unknown;
  params?: Record<string, unknown>;
}) => Promise<any>;

// Re-export the interface so hooks/components can import from this file.
export interface HousekeepingType {
  id: string;
  hotelId: string;
  type: string;
  description: string;
  urgency?: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    totalCount: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
}

export const getHousekeepingTypes = async (
  callApi: CallApiFn,
  page: number = 1,
  limit: number = 20,
  search: string = ""
): Promise<PaginatedResponse<HousekeepingType>> => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("limit", limit.toString());
  if (search.trim()) {
    params.append("search", search.trim());
  }

  const response = await callApi({
    method: "GET",
    url: `/management/housekeeping/types?${params.toString()}`,
  });

  const meta = response.data.meta || {
    totalCount: 0,
    page,
    limit,
    totalPages: 1,
  };

  return {
    data: (response.data.data || []) as HousekeepingType[],
    meta: {
      totalCount: meta.totalCount,
      page: meta.page,
      limit: meta.limit,
      totalPages: meta.totalPages,
    },
  };
};

export const createHousekeepingType = async (
  callApi: CallApiFn,
  housekeeping: Omit<HousekeepingType, "id" | "createdAt" | "updatedAt">
): Promise<HousekeepingType> => {
  const response = await callApi({
    method: "POST",
    url: "/management/housekeeping/types",
    data: {
      type: housekeeping.type,
      description: housekeeping.description,
      urgency: housekeeping.urgency,
    },
  });

  return response.data.data as HousekeepingType;
};

export const updateHousekeepingType = async (
  callApi: CallApiFn,
  id: string,
  housekeeping: Pick<HousekeepingType, "type" | "description" | "urgency"> & {
    hotelId?: string;
  }
): Promise<HousekeepingType> => {
  const response = await callApi({
    method: "PUT",
    url: `/management/housekeeping/types/${id}`,
    data: {
      type: housekeeping.type,
      description: housekeeping.description,
      urgency: housekeeping.urgency,
    },
  });

  return response.data.data as HousekeepingType;
};

export const deleteHousekeepingType = async (
  callApi: CallApiFn,
  id: string
): Promise<void> => {
  await callApi({
    method: "DELETE",
    url: `/management/housekeeping/types/${id}`,
  });
};
